import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { bffApiClient } from '@api/bff/client';
import { useApiToast } from '@hooks/useApiToast';
import type {
  Script,
  CreateScriptRequest,
  UpdateScriptRequest,
  ScriptPaginatedResponse
} from '../types';

const SCRIPTS_QUERY_KEY = 'scripts';

const scriptKeys = {
  all: [SCRIPTS_QUERY_KEY] as const,
  list: (page: number, pageSize: number) =>
    [SCRIPTS_QUERY_KEY, 'list', page, pageSize] as const,
  masters: (page: number, pageSize: number) =>
    [SCRIPTS_QUERY_KEY, 'masters', page, pageSize] as const,
  children: (parentId: string) =>
    [SCRIPTS_QUERY_KEY, 'children', parentId] as const,
  byId: (id: string) => [SCRIPTS_QUERY_KEY, id] as const,
  byGlobalId: (globalId: string) =>
    [SCRIPTS_QUERY_KEY, 'globalId', globalId] as const
};

export const useScriptsQuery = (page = 1, pageSize = 50) => {
  return useQuery<ScriptPaginatedResponse, Error>({
    queryKey: scriptKeys.list(page, pageSize),
    queryFn: async () => {
      const response = await bffApiClient.get<ScriptPaginatedResponse>(
        '/api/mqtt-scripts',
        {
          params: { pageNumber: page, pageSize }
        }
      );
      return response.data;
    },
    staleTime: 30_000
  });
};

export const useMasterScriptsQuery = (page = 1, pageSize = 50) => {
  return useQuery<ScriptPaginatedResponse, Error>({
    queryKey: scriptKeys.masters(page, pageSize),
    queryFn: async () => {
      const response = await bffApiClient.get<ScriptPaginatedResponse>(
        '/api/mqtt-scripts',
        {
          params: { pageNumber: page, pageSize, scriptType: 'MASTER' }
        }
      );
      return response.data;
    },
    staleTime: 30_000
  });
};

export const useChildScriptsQuery = (parentId: string, enabled = true) => {
  return useQuery<Script[], Error>({
    queryKey: scriptKeys.children(parentId),
    queryFn: async () => {
      const response = await bffApiClient.get<Script[]>(
        `/api/mqtt-scripts/${parentId}/children`
      );
      return response.data;
    },
    enabled: enabled && !!parentId,
    staleTime: 30_000
  });
};

export const useScriptByIdQuery = (id: string, enabled = true) => {
  return useQuery<Script, Error>({
    queryKey: scriptKeys.byId(id),
    queryFn: async () => {
      const response = await bffApiClient.get<Script>(
        `/api/mqtt-scripts/${id}`
      );
      return response.data;
    },
    enabled: enabled && !!id,
    staleTime: 30_000
  });
};

export const useCreateScript = () => {
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useApiToast();

  return useMutation<Script, Error, CreateScriptRequest>({
    mutationFn: async (dto: CreateScriptRequest) => {
      const response = await bffApiClient.post<Script>(
        '/api/mqtt-scripts',
        dto
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: scriptKeys.all });
      showSuccess('scripts.createSuccess');
    },
    onError: error => {
      showError('scripts.createError', error);
    }
  });
};

export const useUpdateScript = () => {
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useApiToast();

  return useMutation<Script, Error, { id: string; dto: UpdateScriptRequest }>({
    mutationFn: async ({ id, dto }) => {
      const response = await bffApiClient.put<Script>(
        `/api/mqtt-scripts/${id}`,
        dto
      );
      return response.data;
    },
    onSuccess: data => {
      queryClient.setQueryData(scriptKeys.byId(data.id), data);
      queryClient.invalidateQueries({ queryKey: scriptKeys.all });
      showSuccess('scripts.saveSuccess');
    },
    onError: error => {
      showError('scripts.saveError', error);
    }
  });
};

export const useDeleteScript = () => {
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useApiToast();

  return useMutation<void, Error, string>({
    mutationFn: async (id: string) => {
      await bffApiClient.delete(`/api/mqtt-scripts/${id}`);
    },
    onSuccess: (_data, deletedId) => {
      // Remove the deleted script from the cache immediately
      queryClient.removeQueries({ queryKey: scriptKeys.byId(deletedId) });
      // Refresh list views so counts update
      queryClient.invalidateQueries({ queryKey: ['scripts', 'list'] });
      queryClient.invalidateQueries({ queryKey: ['scripts', 'masters'] });
      // Children are invalidated by the caller (e.g. ScriptProfilePage)
      // so we don't need to invalidate scriptKeys.all, which would also
      // refetch the master script and race with pending autosaves.
      showSuccess('scripts.deleteSuccess');
    },
    onError: error => {
      showError('scripts.deleteError', error);
    }
  });
};

export const useDeleteScripts = () => {
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useApiToast();

  return useMutation<{ deletedCount: number }, Error, string[]>({
    mutationFn: async (ids: string[]) => {
      const response = await bffApiClient.delete<{ deletedCount: number }>(
        '/api/mqtt-scripts',
        {
          data: { ids }
        }
      );
      return response.data;
    },
    onSuccess: (_data, deletedIds) => {
      // Remove deleted scripts from the cache
      for (const id of deletedIds) {
        queryClient.removeQueries({ queryKey: scriptKeys.byId(id) });
      }
      // Refresh list views so counts update
      queryClient.invalidateQueries({ queryKey: ['scripts', 'list'] });
      queryClient.invalidateQueries({ queryKey: ['scripts', 'masters'] });
      // Children are invalidated by the caller
      showSuccess('scripts.deleteSuccess');
    },
    onError: error => {
      showError('scripts.deleteError', error);
    }
  });
};
